import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { collection, addDoc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage } from "../../../firebase";
import { useUserAuth } from "../../../context/userAuthContext";
import DocumentScanner from "./DocumentScanner";
import {
  ChevronLeft,
  Save,
  User,
  Calendar,
  MapPin,
  FileText,
  AlertCircle,
  CheckCircle,
  Loader2,
  Upload,
  X,
  Phone,
  Mail,
  Shield,
  Plane,
  Activity,
  UserPlus,
  CreditCard,
  Sparkles,
  Zap,
  Globe,
  Camera,
  Scan,
} from "lucide-react";

const TravelInsuranceForm = () => {
  const { user } = useUserAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "" });
  const [uploadingINE, setUploadingINE] = useState(false);
  const [uploadingPassport, setUploadingPassport] = useState(false);
  const [ineFile, setIneFile] = useState(null);
  const [inePreview, setInePreview] = useState(null);
  const [passportFrontFile, setPassportFrontFile] = useState(null);
  const [passportFrontPreview, setPassportFrontPreview] = useState(null);
  const [passportBackFile, setPassportBackFile] = useState(null);
  const [passportBackPreview, setPassportBackPreview] = useState(null);
  const [showScanner, setShowScanner] = useState(false);
  const [scannerConfig, setScannerConfig] = useState({ type: "", side: "" });

  const [formData, setFormData] = useState({
    nombreCompleto: "",
    fechaNacimiento: "",
    fechaSalida: "",
    fechaRegreso: "",
    lugaresVisita: "",
    actividadExtrema: "",
    tieneActividadExtrema: false,
    beneficiario: "",
    contactoEmergencia: "",
    celularEmergencia: "",
    requiereFactura: false,
    csf: "",
    notas: "",
  });

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleFileChange = async (e, type = "ine") => {
    const file = e.target.files[0];
    if (!file) return;

    // Validar tipo de archivo
    if (!file.type.startsWith("image/")) {
      setMessage({
        text: "Por favor, selecciona una imagen válida",
        type: "error",
      });
      return;
    }

    // Validar tamaño (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setMessage({
        text: "La imagen debe ser menor a 5MB",
        type: "error",
      });
      return;
    }

    if (type === "ine") {
      setIneFile(file);
      setInePreview(URL.createObjectURL(file));
    } else if (type === "passportFront") {
      setPassportFrontFile(file);
      setPassportFrontPreview(URL.createObjectURL(file));
    } else if (type === "passportBack") {
      setPassportBackFile(file);
      setPassportBackPreview(URL.createObjectURL(file));
    }
  };

  const handleScanCapture = (file, type, side) => {
    if (type === "ine") {
      setIneFile(file);
      setInePreview(URL.createObjectURL(file));
    } else if (type === "passport") {
      if (side === "front") {
        setPassportFrontFile(file);
        setPassportFrontPreview(URL.createObjectURL(file));
      } else {
        setPassportBackFile(file);
        setPassportBackPreview(URL.createObjectURL(file));
      }
    }
    setShowScanner(false);
  };

  const openScanner = (type, side) => {
    setScannerConfig({ type, side });
    setShowScanner(true);
  };

  const removeFile = (type) => {
    if (type === "ine") {
      setIneFile(null);
      setInePreview(null);
    } else if (type === "passportFront") {
      setPassportFrontFile(null);
      setPassportFrontPreview(null);
    } else if (type === "passportBack") {
      setPassportBackFile(null);
      setPassportBackPreview(null);
    }
  };

  const uploadFile = async (file, path) => {
    if (!file || !user) return null;
    try {
      const fileRef = ref(storage, path);
      await uploadBytes(fileRef, file);
      const downloadURL = await getDownloadURL(fileRef);
      return downloadURL;
    } catch (error) {
      console.error("Error al subir archivo:", error);
      throw error;
    }
  };

  const uploadINE = async () => {
    if (!ineFile || !user) return null;
    setUploadingINE(true);
    try {
      return await uploadFile(
        ineFile,
        `policies/${user.uid}/ine/${Date.now()}_${ineFile.name}`
      );
    } finally {
      setUploadingINE(false);
    }
  };

  const uploadPassport = async () => {
    if (!passportFrontFile || !passportBackFile || !user) return null;
    setUploadingPassport(true);
    try {
      const frontUrl = await uploadFile(
        passportFrontFile,
        `policies/${user.uid}/passport/front_${Date.now()}_${passportFrontFile.name}`
      );
      const backUrl = await uploadFile(
        passportBackFile,
        `policies/${user.uid}/passport/back_${Date.now()}_${passportBackFile.name}`
      );
      return { frontUrl, backUrl };
    } finally {
      setUploadingPassport(false);
    }
  };

  const validateForm = () => {
    if (!formData.nombreCompleto.trim()) {
      return "El nombre completo es requerido";
    }
    if (!formData.fechaNacimiento) {
      return "La fecha de nacimiento es requerida";
    }
    if (!formData.fechaSalida) {
      return "La fecha de salida es requerida";
    }
    if (!formData.fechaRegreso) {
      return "La fecha de regreso es requerida";
    }
    if (new Date(formData.fechaSalida) >= new Date(formData.fechaRegreso)) {
      return "La fecha de regreso debe ser posterior a la fecha de salida";
    }
    if (!formData.lugaresVisita.trim()) {
      return "Debes especificar los lugares que visitarás";
    }
    if (!formData.beneficiario.trim()) {
      return "El beneficiario es requerido";
    }
    if (!formData.contactoEmergencia.trim()) {
      return "El contacto de emergencia es requerido";
    }
    if (!formData.celularEmergencia.trim()) {
      return "El celular de emergencia es requerido";
    }
    if (formData.requiereFactura && !formData.csf.trim()) {
      return "El CSF es requerido cuando se solicita factura";
    }
    if (!ineFile) {
      return "Debes adjuntar tu INE";
    }
    if (!passportFrontFile || !passportBackFile) {
      return "Debes adjuntar ambas caras de tu pasaporte (frente y reverso)";
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationError = validateForm();
    if (validationError) {
      setMessage({ text: validationError, type: "error" });
      return;
    }

    setLoading(true);
    setMessage({ text: "", type: "" });

    try {
      // Subir documentos
      const ineUrl = await uploadINE();
      const passportUrls = await uploadPassport();

      // Guardar solicitud en Firestore
      const policyData = {
        ...formData,
        ineUrl,
        passportFrontUrl: passportUrls.frontUrl,
        passportBackUrl: passportUrls.backUrl,
        userId: user.uid,
        userEmail: user.email,
        tipoPoliza: "seguro_viajero",
        estado: "pendiente",
        createdAt: new Date(),
        updatedAt: new Date(),
      };

      await addDoc(collection(db, "policies"), policyData);

      setMessage({
        text: "¡Solicitud enviada exitosamente! Te contactaremos pronto.",
        type: "success",
      });

      // Limpiar formulario
      setTimeout(() => {
        navigate("/polizas");
      }, 2000);
    } catch (error) {
      console.error("Error al enviar solicitud:", error);
      setMessage({
        text: "Error al enviar la solicitud. Por favor, intenta nuevamente.",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-slate-50 via-white to-slate-50">
        <div className="text-center bg-white rounded-2xl shadow-xl p-8 max-w-md w-full border border-slate-200">
          <AlertCircle className="w-16 h-16 text-[#096365] mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">
            Acceso no autorizado
          </h2>
          <p className="text-[#096365]">Por favor inicia sesión para continuar</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50 pb-20 sm:pb-8">
      {/* Header */}
      <div className="bg-white/95 backdrop-blur-lg border-b border-slate-200/50 sticky top-16 z-30 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-2 sm:py-3">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/polizas")}
              className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors"
            >
              <ChevronLeft className="w-5 h-5 text-slate-600" />
            </button>
            <div className="flex items-center gap-2.5">
              <div className="p-2 bg-gradient-to-r from-[#218e01] to-[#5fc500] rounded-lg shadow-md">
                <Plane className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-lg sm:text-xl lg:text-2xl font-bold bg-gradient-to-r from-[#218e01] to-[#5fc500] bg-clip-text text-transparent">
                  Seguro de Viajero
                </h1>
                <p className="text-xs text-slate-600">
                  Completa el formulario para solicitar tu seguro
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        {/* Message Alert */}
        {message.text && (
          <div
            className={`mb-8 p-4 rounded-xl border-2 flex items-center gap-3 ${
              message.type === "success"
                ? "bg-green-50 border-green-200 text-green-800"
                : "bg-red-50 border-red-200 text-red-800"
            }`}
          >
            {message.type === "success" ? (
              <CheckCircle className="w-5 h-5 flex-shrink-0" />
            ) : (
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
            )}
            <p className="font-medium">{message.text}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-12">
          {/* Información Personal */}
          <div className="space-y-6">
            <div className="flex items-center gap-3 pb-4 border-b-2 border-slate-200">
              <div className="p-2 bg-gradient-to-r from-[#218e01] to-[#5fc500] rounded-lg">
                <User className="w-5 h-5 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900">
                Información Personal
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Nombre Completo <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="nombreCompleto"
                  value={formData.nombreCompleto}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-2 focus:ring-[#5fc500] focus:border-[#5fc500] transition-all font-medium text-sm bg-white"
                  placeholder="Ej: Juan Pérez García"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Fecha de Nacimiento <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Calendar className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type="date"
                    name="fechaNacimiento"
                    value={formData.fechaNacimiento}
                    onChange={handleInputChange}
                    max={new Date().toISOString().split("T")[0]}
                    className="w-full pl-12 pr-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-2 focus:ring-[#5fc500] focus:border-[#5fc500] transition-all font-medium text-sm bg-white"
                    required
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Información del Viaje */}
          <div className="space-y-6">
            <div className="flex items-center gap-3 pb-4 border-b-2 border-slate-200">
              <div className="p-2 bg-gradient-to-r from-[#5fc500] to-[#11653a] rounded-lg">
                <Plane className="w-5 h-5 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900">
                Información del Viaje
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Fecha de Salida <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Calendar className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type="date"
                    name="fechaSalida"
                    value={formData.fechaSalida}
                    onChange={handleInputChange}
                    min={new Date().toISOString().split("T")[0]}
                    className="w-full pl-12 pr-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-2 focus:ring-[#5fc500] focus:border-[#5fc500] transition-all font-medium text-sm bg-white"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Fecha de Regreso a México{" "}
                  <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Calendar className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type="date"
                    name="fechaRegreso"
                    value={formData.fechaRegreso}
                    onChange={handleInputChange}
                    min={formData.fechaSalida || new Date().toISOString().split("T")[0]}
                    className="w-full pl-12 pr-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-2 focus:ring-[#5fc500] focus:border-[#5fc500] transition-all font-medium text-sm bg-white"
                    required
                  />
                </div>
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-semibold text-slate-700 mb-2">
                Lugares que Visitará <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <MapPin className="absolute left-4 top-4 w-5 h-5 text-slate-400" />
                <textarea
                  name="lugaresVisita"
                  value={formData.lugaresVisita}
                  onChange={handleInputChange}
                  rows={3}
                  className="w-full pl-12 pr-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-2 focus:ring-[#5fc500] focus:border-[#5fc500] transition-all font-medium text-sm bg-white resize-none"
                  placeholder="Ej: París, Francia; Londres, Reino Unido; Roma, Italia"
                  required
                />
              </div>
            </div>

            <div>
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  name="tieneActividadExtrema"
                  checked={formData.tieneActividadExtrema}
                  onChange={handleInputChange}
                  className="w-5 h-5 text-[#5fc500] border-2 border-slate-300 rounded focus:ring-2 focus:ring-[#5fc500]"
                />
                <span className="text-sm font-semibold text-slate-700">
                  Realizaré actividades extremas durante el viaje
                </span>
              </label>

              {formData.tieneActividadExtrema && (
                <div className="mt-4">
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Describe las actividades extremas
                  </label>
                  <div className="relative">
                    <Activity className="absolute left-4 top-4 w-5 h-5 text-slate-400" />
                    <textarea
                      name="actividadExtrema"
                      value={formData.actividadExtrema}
                      onChange={handleInputChange}
                      rows={3}
                      className="w-full pl-12 pr-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-2 focus:ring-[#5fc500] focus:border-[#5fc500] transition-all font-medium text-sm bg-white resize-none"
                      placeholder="Ej: Paracaidismo, buceo, escalada, etc."
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Documentos */}
          <div className="space-y-6">
            <div className="flex items-center gap-3 pb-4 border-b-2 border-slate-200">
              <div className="p-2 bg-gradient-to-r from-[#11653a] to-[#096365] rounded-lg">
                <FileText className="w-5 h-5 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900">Documentos</h2>
            </div>

            {/* INE */}
            <div className="mb-6">
              <label className="block text-sm font-semibold text-slate-700 mb-2">
                Adjuntar INE <span className="text-red-500">*</span>
              </label>
              {!inePreview ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => openScanner("ine", "front")}
                    className="flex flex-col items-center justify-center h-32 border-2 border-dashed border-slate-300 rounded-xl cursor-pointer hover:border-[#5fc500] hover:bg-slate-50 transition-all group"
                  >
                    <Scan className="w-8 h-8 mb-2 text-slate-400 group-hover:text-[#5fc500] transition-colors" />
                    <p className="text-xs font-semibold text-slate-700">
                      Escanear con Cámara
                    </p>
                  </button>
                  <label className="flex flex-col items-center justify-center h-32 border-2 border-dashed border-slate-300 rounded-xl cursor-pointer hover:border-[#5fc500] hover:bg-slate-50 transition-all group">
                    <Upload className="w-8 h-8 mb-2 text-slate-400 group-hover:text-[#5fc500] transition-colors" />
                    <p className="text-xs font-semibold text-slate-700">
                      Subir desde Archivo
                    </p>
                    <input
                      type="file"
                      accept="image/*,.pdf"
                      onChange={(e) => handleFileChange(e, "ine")}
                      className="hidden"
                    />
                  </label>
                </div>
              ) : (
                <div className="relative">
                  <div className="border-2 border-slate-300 rounded-xl p-4 bg-slate-50">
                    <div className="flex items-center gap-4">
                      <div className="w-20 h-20 rounded-lg overflow-hidden bg-white border-2 border-slate-200">
                        <img
                          src={inePreview}
                          alt="INE Preview"
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="flex-1">
                        <p className="font-semibold text-slate-900">
                          {ineFile?.name}
                        </p>
                        <p className="text-sm text-slate-500">
                          {(ineFile?.size / 1024).toFixed(2)} KB
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeFile("ine")}
                        className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <X className="w-5 h-5 text-red-500" />
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Pasaporte */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">
                Adjuntar Pasaporte (Ambos Lados) <span className="text-red-500">*</span>
              </label>
              
              {/* Pasaporte Frente */}
              <div className="mb-4">
                <p className="text-xs font-medium text-slate-600 mb-2">Frente del Pasaporte</p>
                {!passportFrontPreview ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => openScanner("passport", "front")}
                      className="flex flex-col items-center justify-center h-32 border-2 border-dashed border-slate-300 rounded-xl cursor-pointer hover:border-[#5fc500] hover:bg-slate-50 transition-all group"
                    >
                      <Scan className="w-8 h-8 mb-2 text-slate-400 group-hover:text-[#5fc500] transition-colors" />
                      <p className="text-xs font-semibold text-slate-700">
                        Escanear con Cámara
                      </p>
                    </button>
                    <label className="flex flex-col items-center justify-center h-32 border-2 border-dashed border-slate-300 rounded-xl cursor-pointer hover:border-[#5fc500] hover:bg-slate-50 transition-all group">
                      <Upload className="w-8 h-8 mb-2 text-slate-400 group-hover:text-[#5fc500] transition-colors" />
                      <p className="text-xs font-semibold text-slate-700">
                        Subir desde Archivo
                      </p>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleFileChange(e, "passportFront")}
                        className="hidden"
                      />
                    </label>
                  </div>
                ) : (
                  <div className="relative">
                    <div className="border-2 border-slate-300 rounded-xl p-4 bg-slate-50">
                      <div className="flex items-center gap-4">
                        <div className="w-20 h-20 rounded-lg overflow-hidden bg-white border-2 border-slate-200">
                          <img
                            src={passportFrontPreview}
                            alt="Pasaporte Frente"
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="flex-1">
                          <p className="font-semibold text-slate-900">
                            {passportFrontFile?.name}
                          </p>
                          <p className="text-sm text-slate-500">
                            {(passportFrontFile?.size / 1024).toFixed(2)} KB
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeFile("passportFront")}
                          className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <X className="w-5 h-5 text-red-500" />
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Pasaporte Reverso */}
              <div>
                <p className="text-xs font-medium text-slate-600 mb-2">Reverso del Pasaporte</p>
                {!passportBackPreview ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => openScanner("passport", "back")}
                      className="flex flex-col items-center justify-center h-32 border-2 border-dashed border-slate-300 rounded-xl cursor-pointer hover:border-[#5fc500] hover:bg-slate-50 transition-all group"
                    >
                      <Scan className="w-8 h-8 mb-2 text-slate-400 group-hover:text-[#5fc500] transition-colors" />
                      <p className="text-xs font-semibold text-slate-700">
                        Escanear con Cámara
                      </p>
                    </button>
                    <label className="flex flex-col items-center justify-center h-32 border-2 border-dashed border-slate-300 rounded-xl cursor-pointer hover:border-[#5fc500] hover:bg-slate-50 transition-all group">
                      <Upload className="w-8 h-8 mb-2 text-slate-400 group-hover:text-[#5fc500] transition-colors" />
                      <p className="text-xs font-semibold text-slate-700">
                        Subir desde Archivo
                      </p>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleFileChange(e, "passportBack")}
                        className="hidden"
                      />
                    </label>
                  </div>
                ) : (
                  <div className="relative">
                    <div className="border-2 border-slate-300 rounded-xl p-4 bg-slate-50">
                      <div className="flex items-center gap-4">
                        <div className="w-20 h-20 rounded-lg overflow-hidden bg-white border-2 border-slate-200">
                          <img
                            src={passportBackPreview}
                            alt="Pasaporte Reverso"
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="flex-1">
                          <p className="font-semibold text-slate-900">
                            {passportBackFile?.name}
                          </p>
                          <p className="text-sm text-slate-500">
                            {(passportBackFile?.size / 1024).toFixed(2)} KB
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeFile("passportBack")}
                          className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <X className="w-5 h-5 text-red-500" />
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Beneficiario y Contacto de Emergencia */}
          <div className="space-y-6">
            <div className="flex items-center gap-3 pb-4 border-b-2 border-slate-200">
              <div className="p-2 bg-gradient-to-r from-[#096365] to-[#5ba49e] rounded-lg">
                <UserPlus className="w-5 h-5 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900">
                Beneficiario y Contacto de Emergencia
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Beneficiario <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <UserPlus className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type="text"
                    name="beneficiario"
                    value={formData.beneficiario}
                    onChange={handleInputChange}
                    className="w-full pl-12 pr-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-2 focus:ring-[#5fc500] focus:border-[#5fc500] transition-all font-medium text-sm bg-white"
                    placeholder="Nombre completo del beneficiario"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Contacto de Emergencia <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type="text"
                    name="contactoEmergencia"
                    value={formData.contactoEmergencia}
                    onChange={handleInputChange}
                    className="w-full pl-12 pr-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-2 focus:ring-[#5fc500] focus:border-[#5fc500] transition-all font-medium text-sm bg-white"
                    placeholder="Nombre completo del contacto"
                    required
                  />
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">
                Celular de Emergencia <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  type="tel"
                  name="celularEmergencia"
                  value={formData.celularEmergencia}
                  onChange={handleInputChange}
                  className="w-full pl-12 pr-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-2 focus:ring-[#5fc500] focus:border-[#5fc500] transition-all font-medium text-sm bg-white"
                  placeholder="+52 55 1234 5678"
                  required
                />
              </div>
            </div>
          </div>

          {/* Facturación */}
          <div className="space-y-6">
            <div className="flex items-center gap-3 pb-4 border-b-2 border-slate-200">
              <div className="p-2 bg-gradient-to-r from-[#5ba49e] to-[#218e01] rounded-lg">
                <CreditCard className="w-5 h-5 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900">Facturación</h2>
            </div>

            <div className="mb-6">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  name="requiereFactura"
                  checked={formData.requiereFactura}
                  onChange={handleInputChange}
                  className="w-5 h-5 text-[#5fc500] border-2 border-slate-300 rounded focus:ring-2 focus:ring-[#5fc500]"
                />
                <span className="text-sm font-semibold text-slate-700">
                  Requiero factura
                </span>
              </label>

              {formData.requiereFactura && (
                <div className="mt-4">
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    CSF (Clave de Seguimiento Fiscal){" "}
                    <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <CreditCard className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <input
                      type="text"
                      name="csf"
                      value={formData.csf}
                      onChange={handleInputChange}
                      className="w-full pl-12 pr-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-2 focus:ring-[#5fc500] focus:border-[#5fc500] transition-all font-medium text-sm bg-white"
                      placeholder="Ingresa tu CSF"
                      required={formData.requiereFactura}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Notas Adicionales */}
          <div className="space-y-6">
            <div className="flex items-center gap-3 pb-4 border-b-2 border-slate-200">
              <div className="p-2 bg-gradient-to-r from-[#218e01] to-[#5fc500] rounded-lg">
                <FileText className="w-5 h-5 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900">
                Notas Adicionales
              </h2>
            </div>

            <textarea
              name="notas"
              value={formData.notas}
              onChange={handleInputChange}
              rows={4}
              className="w-full px-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-2 focus:ring-[#5fc500] focus:border-[#5fc500] transition-all font-medium text-sm bg-white resize-none"
              placeholder="Información adicional que consideres relevante..."
            />
          </div>

          {/* Submit Button */}
          <div className="flex flex-col sm:flex-row gap-4">
            <button
              type="button"
              onClick={() => navigate("/polizas")}
              className="flex-1 px-6 py-4 border-2 border-slate-300 rounded-xl font-bold text-slate-700 hover:bg-slate-50 transition-all"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading || uploadingINE || uploadingPassport}
              className="flex-1 px-6 py-4 bg-gradient-to-r from-[#218e01] to-[#5fc500] rounded-xl font-bold text-white shadow-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading || uploadingINE || uploadingPassport ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  {uploadingINE || uploadingPassport
                    ? "Subiendo documentos..."
                    : "Enviando solicitud..."}
                </>
              ) : (
                <>
                  <Save className="w-5 h-5" />
                  Enviar Solicitud
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Document Scanner Modal */}
      {showScanner && (
        <DocumentScanner
          onCapture={(file) =>
            handleScanCapture(file, scannerConfig.type, scannerConfig.side)
          }
          onClose={() => setShowScanner(false)}
          side={scannerConfig.side}
          documentType={
            scannerConfig.type === "passport" ? "Pasaporte" : "INE"
          }
        />
      )}
    </div>
  );
};

export default TravelInsuranceForm;

